import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import JourneyBuilder from './JourneyBuilder';
import ClashCard from '../ClashCard';
import { journeyJob, commsJobs, loadCommsSettings, switchOffComms, JOB_WORDS } from '../overlaps';
import { JOURNEY_RECIPES } from './journeyRecipes';
import { loadJourneys, saveJourney, deleteJourney, triggerLabel, hydrateJourneys } from './journeysStore';

/**
 * The journeys list: recipe gallery front door, modern cards with
 * Ready/Paused toggles, and the timeline builder. Lives on the dedicated
 * Journeys tab; definitions are saved on the server (journey/*) and run by
 * the journey engine (journeys-wake / journeys-scan crons).
 */

const ink = '#1A1A1E';
const muted = '#8E8E93';
const line = '#ECECF0';

const generateStepId = () => `s-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const cloneRecipeJourney = (recipe) => ({
  ...recipe.journey,
  steps: recipe.journey.steps.map((s) => ({ ...s, id: generateStepId(), ...(s.blocks ? { blocks: s.blocks.map((b) => ({ ...b })) } : {}) })),
});

const STEP_ICONS = {
  email: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
  ),
  sms: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
  ),
  filter: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 4a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v2.6a1 1 0 0 1-.3.7l-6.4 6.4a1 1 0 0 0-.3.7V17l-4 4v-6.6a1 1 0 0 0-.3-.7L3.3 7.3A1 1 0 0 1 3 6.6V4z" /></svg>
  ),
};

const journeyDurationDays = (journey) => journey.steps.reduce((n, st) => n + (st.type === 'wait' ? (st.days || 0) : 0), 0);

const durationLabel = (journey) => {
  const days = journeyDurationDays(journey);
  if (!days) return 'runs same day';
  if (days % 7 === 0) return `runs over ${days / 7} week${days / 7 > 1 ? 's' : ''}`;
  return `runs over ${days} day${days > 1 ? 's' : ''}`;
};

// Tiny visual of the journey's shape: trigger dot, then chips for each step
const MiniTimeline = ({ journey }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#29282D', flexShrink: 0 }} title="Trigger" />
    {journey.steps.map((st) => (
      <React.Fragment key={st.id}>
        <span style={{ width: 8, height: 1.5, background: '#D6D6DC', flexShrink: 0 }} />
        {st.type === 'wait' ? (
          <span style={{ fontSize: 9.5, fontWeight: 700, color: '#8E8E93', background: '#F2F2F7', borderRadius: 100, padding: '2px 7px', flexShrink: 0 }}>
            {st.days}d
          </span>
        ) : (
          <span style={{
            width: 22, height: 22, borderRadius: 7, flexShrink: 0,
            background: 'rgba(0,126,160,0.1)', color: '#007EA0',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
          }} title={st.type === 'email' ? 'Email' : st.type === 'sms' ? 'Text' : 'Only continue if'}>
            {STEP_ICONS[st.type]}
          </span>
        )}
      </React.Fragment>
    ))}
    {journey.goalExit && (
      <>
        <span style={{ width: 8, height: 1.5, background: '#D6D6DC', flexShrink: 0 }} />
        <span style={{
          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
          background: '#007EA0', color: '#fff',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
        }} title="Ends early when the client books">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
        </span>
      </>
    )}
  </span>
);

const stepSummary = (journey) => {
  const counts = { email: 0, sms: 0 };
  journey.steps.forEach((s) => { if (counts[s.type] !== undefined) counts[s.type] += 1; });
  const bits = [];
  if (counts.email) bits.push(`${counts.email} email${counts.email > 1 ? 's' : ''}`);
  if (counts.sms) bits.push(`${counts.sms} text${counts.sms > 1 ? 's' : ''}`);
  return bits.join(', ') || 'No messages yet';
};

const JourneysSection = ({ addSignal = 0, openJourneyId = null, openRecipe = null, onOpenConsumed, managedBySalon = false }) => {
  const dispatch = useDispatch();
  // Switching on a journey that does a Communications automation's job
  const [clash, setClash] = useState(null); // { journey, found, settings }
  // Salon-controlled marketing: the salon runs this stylist's marketing. Their
  // own journeys stay listed but are read-only and do not run (the server
  // refuses saves and switching one on, and stops any run in flight).
  const managed = !!managedBySalon;
  const salonId = localStorage.getItem('salonId');
  const [journeys, setJourneys] = useState(() => loadJourneys(salonId));
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [editing, setEditing] = useState(null); // journey object or 'blank'
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // The page's top-nav Add button bumps addSignal to open the gallery
  useEffect(() => {
    if (addSignal > 0 && !managed) setGalleryOpen(true);
  }, [addSignal, managed]);

  // Arriving here to open one specific journey — from the assistant, which
  // drafted it and now has to be able to hand it over. Waits for the list to
  // hydrate, because on a cold load the cache may not hold it yet; and clears
  // the routing state once used, so a back-navigation does not reopen it.
  useEffect(() => {
    if (!openJourneyId) return;
    const found = journeys.find((j) => String(j.id) === String(openJourneyId));
    if (!found) return;
    if (!managed) setEditing(found);
    if (onOpenConsumed) onOpenConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openJourneyId, journeys]);

  // Hydrate from the backend on mount (localStorage is only the cache now).
  useEffect(() => {
    let alive = true;
    hydrateJourneys(salonId).then((list) => { if (alive) setJourneys(list); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Saves are write-through; ids converge to server ids shortly after a
  // create — refresh the list from the cache a beat later so the next edit
  // targets the server id.
  const refreshSoon = () => setTimeout(() => setJourneys(loadJourneys(salonId)), 800);

  // Arriving from Bailey ("Set up the journey"): that recipe, ready to check
  useEffect(() => {
    if (!openRecipe || managed) return;
    const recipe = JOURNEY_RECIPES.find((r) => r.id === openRecipe);
    if (recipe) setEditing(cloneRecipeJourney(recipe));
    if (onOpenConsumed) onOpenConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRecipe]);

  // A journey going live that Communications already covers: say so, and
  // offer to switch the Communications one off
  const checkClash = (journey) => {
    const job = journeyJob(journey);
    if (!job || journey.status !== 'ready') return;
    loadCommsSettings(dispatch).then((settings) => {
      const found = commsJobs(settings).filter((f) => f.job === job);
      if (found.length) setClash({ journey, found, settings });
    });
  };

  const handleSave = (journey) => {
    saveJourney(salonId, journey);
    setJourneys(loadJourneys(salonId));
    setEditing(null);
    refreshSoon();
    checkClash(journey);
  };

  const toggleStatus = (journey) => {
    const next = { ...journey, status: journey.status === 'ready' ? 'paused' : 'ready' };
    saveJourney(salonId, next);
    setJourneys(loadJourneys(salonId));
    refreshSoon();
    checkClash(next);
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', gap: 14 }}>
        {journeys.map((j) => (
          <div
            key={j.id}
            style={{
              background: '#fff', border: `1px solid ${line}`, borderRadius: 14, padding: 18,
              display: 'flex', flexDirection: 'column', minHeight: 150,
              transition: 'box-shadow 0.15s, border-color 0.15s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; e.currentTarget.style.borderColor = '#DEDEE2'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = line; }}
          >
            <div style={{ cursor: managed ? 'default' : 'pointer', flex: 1 }} onClick={() => { if (!managed) setEditing(j); }}>
              <p style={{ fontSize: 15, fontWeight: 700, color: ink, margin: '0 0 5px', letterSpacing: '-0.1px' }}>{j.name}</p>
              <p style={{ fontSize: 12.5, color: muted, lineHeight: 1.5, margin: '0 0 12px' }}>{triggerLabel(j.trigger)} · {stepSummary(j)}</p>
              <MiniTimeline journey={j} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 14 }}>
              <span style={{
                fontSize: 10.5, fontWeight: 700, borderRadius: 100, padding: '3px 9px',
                color: j.status === 'ready' && !managed ? '#007EA0' : muted,
                background: j.status === 'ready' && !managed ? 'rgba(0,126,160,0.1)' : '#F2F2F7'
              }} title={managed ? 'Not running. The salon runs your marketing.' : undefined}>
                {managed ? 'On hold' : j.status === 'ready' ? 'Ready' : 'Paused'}
              </span>
              {!managed && (
              <button
                type="button"
                onClick={() => {
                  const copy = { ...j, id: undefined, name: `${j.name} copy`, status: 'paused', steps: j.steps.map((st) => ({ ...st, id: generateStepId(), ...(st.hasEmailHtml && !st.emailHtml ? { emailHtmlFrom: { journeyId: j.id, stepId: st.id } } : {}) })) };
                  saveJourney(salonId, copy);
                  setJourneys(loadJourneys(salonId));
                }}
                aria-label="Duplicate journey"
                title="Duplicate"
                style={{ border: 'none', background: 'none', color: '#C7C7CC', cursor: 'pointer', padding: 2, display: 'inline-flex' }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
              </button>
              )}
              <button
                type="button"
                onClick={() => setConfirmDeleteId(j.id)}
                aria-label="Delete journey"
                style={{ border: 'none', background: 'none', color: '#C7C7CC', cursor: 'pointer', padding: 2, display: 'inline-flex' }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
              </button>
              {!managed && (
              <span style={{ marginLeft: 'auto' }}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={j.status === 'ready'}
                  onClick={() => toggleStatus(j)}
                  style={{
                    width: 44, height: 26, borderRadius: 100, border: 'none', cursor: 'pointer', padding: 2,
                    background: j.status === 'ready' ? '#007EA0' : '#DEDEE2', transition: 'background 0.15s'
                  }}
                >
                  <span style={{
                    display: 'block', width: 22, height: 22, borderRadius: '50%', background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'transform 0.15s',
                    transform: j.status === 'ready' ? 'translateX(18px)' : 'translateX(0)'
                  }} />
                </button>
              </span>
              )}
            </div>
          </div>
        ))}

        {!managed && (
        <button
          type="button"
          onClick={() => setGalleryOpen(true)}
          style={{
            minHeight: 150, border: '1.5px dashed #D6D6DC', borderRadius: 14, background: '#fff',
            cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 10, transition: 'border-color 0.15s'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#007EA0'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#D6D6DC'; }}
        >
          <span style={{
            width: 44, height: 44, borderRadius: '50%', background: 'rgba(0,126,160,0.1)', color: '#007EA0',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
          </span>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#4B4B52' }}>Create New Journey</span>
        </button>
        )}
      </div>

      {managed && journeys.length === 0 && (
        <p style={{ fontSize: 13.5, color: muted, margin: '4px 2px 0' }}>
          No journeys of your own. The salon&rsquo;s journeys look after your clients.
        </p>
      )}

      {!managed && journeys.some((j) => j.status === 'ready') && (
        <p style={{ fontSize: '11.5px', color: muted, margin: '12px 2px 0' }}>
          Ready journeys run by themselves: each client joins when they meet the start.
        </p>
      )}

      {/* Recipe gallery */}
      {galleryOpen && !managed && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 100012, background: 'rgba(26,26,30,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setGalleryOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 760, maxHeight: '90vh', overflowY: 'auto', background: '#fff', borderRadius: 18, padding: 28 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <h2 style={{ fontSize: 21, fontWeight: 700, color: ink, margin: '0 0 4px' }}>Start a journey</h2>
                <p style={{ fontSize: 13.5, color: muted, margin: '0 0 20px' }}>Pick an outcome and make it yours, or build from scratch.</p>
              </div>
              <button type="button" onClick={() => setGalleryOpen(false)} aria-label="Close" style={{ border: 'none', background: '#F2F2F7', borderRadius: 10, width: 34, height: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1A1A1E" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))', gap: 12 }}>
              {JOURNEY_RECIPES.map((recipe) => (
                <button
                  key={recipe.id}
                  type="button"
                  onClick={() => { setGalleryOpen(false); setEditing(cloneRecipeJourney(recipe)); }}
                  style={{
                    textAlign: 'left', padding: 16, background: '#fff', border: `1px solid ${line}`,
                    borderRadius: 14, cursor: 'pointer', fontFamily: 'inherit',
                    transition: 'border-color 0.14s, box-shadow 0.14s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#007EA0'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,126,160,0.1)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = line; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                    <span style={{ fontSize: 14.5, fontWeight: 700, color: ink }}>{recipe.name}</span>
                    {recipe.popular && (
                      <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#007EA0', background: 'rgba(0,126,160,0.1)', borderRadius: 100, padding: '2px 7px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Most popular
                      </span>
                    )}
                  </span>
                  <span style={{ display: 'block', fontSize: 12, color: muted, lineHeight: 1.5, marginBottom: 12 }}>{recipe.description}</span>
                  <MiniTimeline journey={recipe.journey} />
                  <span style={{ display: 'block', fontSize: 10.5, fontWeight: 600, color: '#C7C7CC', marginTop: 8 }}>
                    {recipe.journey.steps.filter((st) => st.type !== 'wait').length} step{recipe.journey.steps.filter((st) => st.type !== 'wait').length !== 1 ? 's' : ''} · {durationLabel(recipe.journey)}
                  </span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => { setGalleryOpen(false); setEditing('blank'); }}
                style={{
                  textAlign: 'center', padding: 16, background: '#fff', border: '1.5px dashed #D6D6DC',
                  borderRadius: 14, cursor: 'pointer', fontFamily: 'inherit', display: 'flex',
                  flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8
                }}
              >
                <span style={{ width: 36, height: 36, borderRadius: '50%', background: '#F2F2F7', color: '#4B4B52', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                </span>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: '#4B4B52' }}>Start from scratch</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Builder */}
      {editing && !managed && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 100013 }}>
          <JourneyBuilder
            initialJourney={editing === 'blank' ? null : editing}
            onSave={handleSave}
            onClose={() => setEditing(null)}
          />
        </div>,
        document.body
      )}

      {clash && ReactDOM.createPortal(
        <ClashCard
          title="Communications does this too"
          text={`“${clash.found.map((f) => f.label).join('” and “')}” on Communications already sends ${JOB_WORDS[journeyJob(clash.journey)]}. With both on, clients get two.`}
          offLabel={clash.found.length === 1 ? `Switch off ${clash.found[0].label}` : 'Switch those off'}
          onKeep={() => setClash(null)}
          onSwitchOff={async () => { await switchOffComms(dispatch, clash.settings, clash.found); setClash(null); }}
        />,
        document.body
      )}

      {/* Delete confirmation */}
      {confirmDeleteId && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 100014, background: 'rgba(26,26,30,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setConfirmDeleteId(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 380, background: '#fff', borderRadius: 16, padding: 24 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: ink, margin: '0 0 6px' }}>Delete this journey?</h3>
            <p style={{ fontSize: 13.5, color: muted, lineHeight: 1.55, margin: '0 0 18px' }}>
              This removes "{journeys.find((j) => j.id === confirmDeleteId)?.name}" permanently. It cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" onClick={() => setConfirmDeleteId(null)} style={{ flex: 1, padding: 11, border: `1px solid ${line}`, borderRadius: 10, background: '#fff', fontSize: 13.5, fontWeight: 600, color: ink, cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { setJourneys(deleteJourney(salonId, confirmDeleteId)); setConfirmDeleteId(null); }}
                style={{ flex: 1, padding: 11, border: 'none', borderRadius: 10, background: '#B14438', fontSize: 13.5, fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default JourneysSection;
